import { useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { formatRelative } from '@/lib/dates'
import { formatMoney } from '@/lib/format'

/**
 * Per-tenant AI usage drill-down. Linked from the per-shop row on
 * /admin/usage. Shows the same KPI strip + daily chart, plus
 * per-model and per-surface cost breakdowns so Mira/Emil can answer
 * "where is this tenant's Anthropic spend going?".
 *
 * Surface key examples: `vin_ocr` (vehicles-from-photo route),
 * `eric_interface.classifier` (Haiku guardrail), `eric_interface.orchestrator`
 * (Sonnet tool-use loop). Future: `eric_outbound`, `estimator`.
 */

type Range = '7d' | '30d' | '90d'

type ModelBucket = {
  model: string
  tokens_in: number
  tokens_out: number
  cost_cad: number
  calls: number
}

type SurfaceBucket = {
  surface: string
  cost_cad: number
  calls: number
  avg_latency_ms: number | null
}

type DayBucket = {
  date: string
  total_cad: number
}

type Resp = {
  range: Range
  days: number
  since: string
  shop: {
    id: string
    name: string | null
    provisioned_at: string | null
    suspended_at: string | null
  }
  kpis: {
    total_cost_cad: number
    delta_pct: number | null
    tokens_in: number
    tokens_out: number
    tokens_total: number
    total_calls: number
    last_called_at: string | null
  }
  daily_series: DayBucket[]
  per_model: ModelBucket[]
  per_surface: SurfaceBucket[]
}

export function AdminUsageDetail() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const { shopId = '' } = useParams<{ shopId: string }>()
  const [range, setRange] = useState<Range>('30d')

  const query = useQuery<Resp>({
    queryKey: ['admin', 'usage', shopId, range],
    enabled: isLoaded && !!shopId,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(
        `/api/admin/usage/${encodeURIComponent(shopId)}?range=${range}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Resp
    },
  })

  const rangeLabelKey =
    range === '7d' ? 'range_7d' : range === '90d' ? 'range_90d' : 'range_30d'
  const rangeLabel = t(`usage.${rangeLabelKey}`)

  const maxDay = useMemo(() => {
    const series = query.data?.daily_series ?? []
    return Math.max(0, ...series.map((d) => d.total_cad))
  }, [query.data])

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/usage"
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          ← {t('usage_detail.back')}
        </Link>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {query.data?.shop.name ?? shopId}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('usage_detail.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangeButton
            current={range}
            value="7d"
            label={t('usage.range_7d')}
            onSelect={setRange}
          />
          <RangeButton
            current={range}
            value="30d"
            label={t('usage.range_30d')}
            onSelect={setRange}
          />
          <RangeButton
            current={range}
            value="90d"
            label={t('usage.range_90d')}
            onSelect={setRange}
          />
        </div>
      </div>

      {query.isError ? (
        <Card className="border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : t('usage.error')}
        </Card>
      ) : null}

      {query.isLoading ? (
        <Card className="p-4 text-sm text-muted-foreground">
          {tCommon('loading')}
        </Card>
      ) : null}

      {query.data ? (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard
              label={t('usage.kpis.total_cost')}
              value={formatMoney(query.data.kpis.total_cost_cad)}
              meta={
                query.data.kpis.delta_pct === null
                  ? t('usage.kpis.delta_none')
                  : query.data.kpis.delta_pct >= 0
                    ? t('usage.kpis.delta_up', {
                        percent: Math.round(query.data.kpis.delta_pct),
                      })
                    : t('usage.kpis.delta_down', {
                        percent: Math.abs(
                          Math.round(query.data.kpis.delta_pct),
                        ),
                      })
              }
              metaTone={
                query.data.kpis.delta_pct === null
                  ? 'muted'
                  : query.data.kpis.delta_pct >= 0
                    ? 'up'
                    : 'down'
              }
              footnote={t('usage.kpis.total_cost_window', {
                range: rangeLabel.toLowerCase(),
              })}
            />
            <KpiCard
              label={t('usage.kpis.tokens')}
              value={formatLargeNumber(query.data.kpis.tokens_total)}
              meta={t('usage.kpis.tokens_breakdown', {
                in: formatLargeNumber(query.data.kpis.tokens_in),
                out: formatLargeNumber(query.data.kpis.tokens_out),
              })}
            />
            <KpiCard
              label={t('usage_detail.kpis.calls')}
              value={String(query.data.kpis.total_calls)}
              meta={
                query.data.kpis.last_called_at
                  ? t('usage_detail.kpis.last_call', {
                      relative: formatRelative(query.data.kpis.last_called_at),
                    })
                  : t('usage.table.never')
              }
            />
            <KpiCard
              label={t('usage_detail.kpis.avg_call')}
              value={
                query.data.kpis.total_calls > 0
                  ? formatMoney(
                      query.data.kpis.total_cost_cad /
                        query.data.kpis.total_calls,
                    )
                  : '—'
              }
              meta={t('usage_detail.kpis.avg_call_meta')}
            />
          </div>

          {/* Daily chart */}
          <Card className="p-5">
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-foreground">
                  {t('usage.chart.header')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('usage.chart.subtitle', {
                    range: rangeLabel.toLowerCase(),
                  })}
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t('usage.chart.today_partial')}
              </div>
            </div>
            <div className="flex h-40 items-end gap-1.5 px-1">
              {query.data.daily_series.map((d, i) => {
                const heightPct =
                  maxDay > 0 ? Math.max((d.total_cad / maxDay) * 100, 2) : 2
                const isToday = i === query.data!.daily_series.length - 1
                return (
                  <div
                    key={d.date}
                    title={`${d.date} · ${formatMoney(d.total_cad)}`}
                    style={{ height: `${heightPct}%` }}
                    className={
                      isToday
                        ? 'flex-1 rounded-sm bg-brand-light'
                        : 'flex-1 rounded-sm bg-brand'
                    }
                  />
                )
              })}
            </div>
          </Card>

          {/* Per-model breakdown */}
          <Card className="overflow-hidden p-0">
            <div className="border-b border-border bg-muted/30 px-4 py-3">
              <div className="text-sm font-semibold text-foreground">
                {t('usage_detail.per_model.header')}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('usage_detail.per_model.subtitle')}
              </div>
            </div>
            {query.data.per_model.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {t('usage_detail.empty')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-2 font-semibold">
                        {t('usage_detail.per_model.model')}
                      </th>
                      <th className="px-4 py-2 text-right font-semibold">
                        {t('usage.table.tokens_in')}
                      </th>
                      <th className="px-4 py-2 text-right font-semibold">
                        {t('usage.table.tokens_out')}
                      </th>
                      <th className="px-4 py-2 text-right font-semibold">
                        {t('usage.table.calls')}
                      </th>
                      <th className="px-4 py-2 text-right font-semibold">
                        {t('usage.table.cost')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.per_model.map((m) => (
                      <tr
                        key={m.model}
                        className="border-t border-border/60"
                      >
                        <td className="px-4 py-2 font-mono text-xs">
                          {m.model}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {m.tokens_in.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {m.tokens_out.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right">{m.calls}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">
                          {formatMoney(m.cost_cad)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Per-surface breakdown */}
          <Card className="overflow-hidden p-0">
            <div className="border-b border-border bg-muted/30 px-4 py-3">
              <div className="text-sm font-semibold text-foreground">
                {t('usage_detail.per_surface.header')}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('usage_detail.per_surface.subtitle')}
              </div>
            </div>
            {query.data.per_surface.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {t('usage_detail.empty')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-2 font-semibold">
                        {t('usage_detail.per_surface.surface')}
                      </th>
                      <th className="px-4 py-2 text-right font-semibold">
                        {t('usage.table.calls')}
                      </th>
                      <th className="px-4 py-2 text-right font-semibold">
                        {t('usage_detail.per_surface.avg_latency')}
                      </th>
                      <th className="px-4 py-2 text-right font-semibold">
                        {t('usage.table.cost')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.per_surface.map((s) => (
                      <tr
                        key={s.surface}
                        className="border-t border-border/60"
                      >
                        <td className="px-4 py-2 font-mono text-xs">
                          {s.surface}
                        </td>
                        <td className="px-4 py-2 text-right">{s.calls}</td>
                        <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                          {s.avg_latency_ms === null
                            ? '—'
                            : `${s.avg_latency_ms} ms`}
                        </td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">
                          {formatMoney(s.cost_cad)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      ) : null}
    </div>
  )
}

function RangeButton({
  current,
  value,
  label,
  onSelect,
}: {
  current: Range
  value: Range
  label: string
  onSelect: (v: Range) => void
}) {
  return (
    <Button
      variant={current === value ? 'default' : 'outline'}
      size="sm"
      onClick={() => onSelect(value)}
    >
      {label}
    </Button>
  )
}

function KpiCard({
  label,
  value,
  meta,
  metaTone,
  footnote,
}: {
  label: string
  value: string
  meta?: string
  metaTone?: 'up' | 'down' | 'muted'
  footnote?: string
}) {
  const metaClass =
    metaTone === 'up'
      ? 'text-emerald-700'
      : metaTone === 'down'
        ? 'text-rose-700'
        : 'text-muted-foreground'
  return (
    <Card className="p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-foreground">{value}</div>
      {meta ? <div className={`mt-1 text-xs ${metaClass}`}>{meta}</div> : null}
      {footnote ? (
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {footnote}
        </div>
      ) : null}
    </Card>
  )
}

function formatLargeNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return n.toString()
}
